import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import { useUIStore } from '../../store/uiStore';
import { useServiceAreasStore } from '../../store/serviceAreasStore';
import { getLeaflet } from './leafletHelpers';
import { createPrefixedLogger } from '../../utils/logger';
import { FHSZ_TIER_COLORS, type FhszTier } from './fhszColors';

const log = createPrefixedLogger('map:fhsz');

interface FhszProperties {
  FHSZ: number;
  FHSZ_Description: FhszTier;
  SRA: 'SRA' | 'LRA';
}

interface FhszFeatureCollection {
  type: 'FeatureCollection';
  features: Feature<Polygon | MultiPolygon, FhszProperties>[];
}

function fhszUrl(areaId: string): string {
  return `/fhsz/${encodeURIComponent(areaId)}.geojson`;
}

// Dedicated pane so toggling FHSZ visibility can't hide other vector layers:
// the default overlayPane (z 400) is shared with the service-area outlines,
// and both hooks toggle by pane display. Sits below overlayPane and the
// marker pane (z 600) so hazard fills never cover an outline or a pin.
const FHSZ_PANE = 'fhszPane';
const FHSZ_PANE_Z_INDEX = '350';

/**
 * Render CAL FIRE Fire Hazard Severity Zone polygons for every active
 * service area, shown/hidden en masse by `uiStore.fhszOverlayEnabled`.
 *
 * Mirrors useServiceAreaPolygons: one `L.GeoJSON` layer per area lives in
 * the shared overlay layer group (same pane as the service-area outlines,
 * a different pane than the structure/post markers, so fills never sit
 * above a pin). Layers are built once on first enable and toggled by pane
 * display rather than removed, so re-enabling never re-fetches. A missing
 * export (area outside California, or not yet exported) 404s once and is
 * remembered so it isn't retried on every toggle.
 *
 * useFireHistoryOverlay.web.ts repeats nearly all of this. Both are worth
 * folding into a shared `useStaticGeojsonOverlay` when a third overlay
 * lands; doing it for two would put this shipped path back under test for
 * nothing the user can see.
 */
export function useFhszOverlay({
  mapRef,
  overlayLayerRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  overlayLayerRef: RefObject<L.LayerGroup | null>;
  isLoaded: boolean;
}): void {
  const enabled = useUIStore((s) => s.fhszOverlayEnabled);
  const areas = useServiceAreasStore((s) => s.areas);
  const activeAreaIdsKey = areas
    .filter((a) => a.active)
    .map((a) => a.id)
    .join(',');

  const layersRef = useRef<Map<string, L.GeoJSON>>(new Map());
  const missesRef = useRef<Set<string>>(new Set());
  const loadingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const overlay = overlayLayerRef.current;
    const map = mapRef.current;
    if (!overlay || !map || !isLoaded || !enabled) return;
    const activeAreaIds = activeAreaIdsKey ? activeAreaIdsKey.split(',') : [];

    const setVisible = (visible: boolean) => {
      for (const layer of layersRef.current.values()) {
        const element = (
          layer as unknown as { getPane?: () => HTMLElement | undefined }
        ).getPane?.();
        if (element) element.style.display = visible ? '' : 'none';
      }
    };

    const buildLayer = (areaId: string, collection: FhszFeatureCollection) => {
      if (layersRef.current.has(areaId)) return;
      const L = getLeaflet();
      if (!map.getPane(FHSZ_PANE)) {
        map.createPane(FHSZ_PANE).style.zIndex = FHSZ_PANE_Z_INDEX;
      }
      const layer = L.geoJSON(collection as never, {
        pane: FHSZ_PANE,
        interactive: false,
        style: (feature) => {
          const tier = feature?.properties?.FHSZ_Description as FhszTier | undefined;
          const palette = tier ? FHSZ_TIER_COLORS[tier] : undefined;
          return {
            fillColor: palette?.fill ?? FHSZ_TIER_COLORS.Moderate.fill,
            color: palette?.stroke ?? FHSZ_TIER_COLORS.Moderate.stroke,
            fillOpacity: 0.35,
            weight: 1,
            opacity: 0.8,
          };
        },
      });
      layer.addTo(overlay);
      layersRef.current.set(areaId, layer);
    };

    let cancelled = false;
    for (const areaId of activeAreaIds) {
      if (
        layersRef.current.has(areaId) ||
        missesRef.current.has(areaId) ||
        loadingRef.current.has(areaId)
      ) {
        continue;
      }
      loadingRef.current.add(areaId);
      fetch(fhszUrl(areaId))
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              missesRef.current.add(areaId);
              return null;
            }
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json() as Promise<FhszFeatureCollection>;
        })
        .then((collection) => {
          if (cancelled || !collection) return;
          buildLayer(areaId, collection);
          setVisible(useUIStore.getState().fhszOverlayEnabled);
        })
        .catch((error) => {
          log.warn(`Failed to load FHSZ overlay for ${areaId}`, error);
        })
        .finally(() => {
          loadingRef.current.delete(areaId);
        });
    }

    setVisible(true);
    return () => {
      cancelled = true;
    };
  }, [enabled, activeAreaIdsKey, isLoaded, mapRef, overlayLayerRef]);

  // Hide (never remove) once disabled — keeps every fetched layer cached so
  // re-enabling the toggle is instant.
  useEffect(() => {
    if (enabled) return;
    for (const layer of layersRef.current.values()) {
      const element = (layer as unknown as { getPane?: () => HTMLElement | undefined }).getPane?.();
      if (element) element.style.display = 'none';
    }
  }, [enabled]);
}
